/**
 * FAT-4 — il bottone «Emetti su Fatture in Cloud», e i quattro esiti.
 *
 * ⛔ STA SUL DETTAGLIO E NON SULLA LISTA, ed è una decisione, non estetica.
 * Emettere è IRREVERSIBILE: partita verso lo SDI non si annulla, si fa una nota
 * di credito. Una lista è il posto dove si tocca in fretta, spesso la riga
 * sbagliata — sul telefono, in furgone, con una mano sola. Un'azione che non si
 * può disfare non sta a un tocco di distanza da una lista. Qui l'artigiano ha
 * appena letto cliente, importi e IVA: è il contesto che l'azione richiede.
 *
 * 🔴 IL QUARTO ESITO NON OFFRE IL RIPROVA. Se la risposta non torna, il
 * documento POTREBBE essere stato creato: un «Riprova» lì è il modo in cui si
 * fanno due fatture per lo stesso lavoro. Il bottone resta disattivato finché
 * l'artigiano non dichiara di aver guardato — non per punirlo, perché è l'unico
 * che può sciogliere il dubbio.
 *
 * La mappa esito → frasi vive in `pages/ficEsiti.js` (funzione pura, provata).
 */
import { useState } from 'react';
import { Send, ExternalLink, AlertTriangle, Check } from 'lucide-react';

import { integrazioni as integrazioniApi, clienti as clientiApi } from '../../utils/api';
import { esitoEmissione, mostraBottoneEmetti, mostraBottoneRiprova, statoEmissioneFIC,
         urlDocumentoFIC, URL_FIC_CLIENTI,
         MURO_DATO_MANCANTE, MURO_FUORI_APP } from '../../pages/ficEsiti';


const TONI = {
  riuscita: { bordo: 'var(--ok)', testo: 'var(--ok)' },
  chiede: { bordo: 'var(--warn)', testo: 'var(--warn)' },
  devia: { bordo: 'var(--warn)', testo: 'var(--warn)' },
  bloccata: { bordo: 'var(--warn)', testo: 'var(--warn)' },
  incerto: { bordo: 'var(--ds-danger)', testo: 'var(--ds-danger)' },
  errore: { bordo: 'var(--ds-danger)', testo: 'var(--ds-danger)' },
};

export default function EmissioneFIC({ fattura, onRefresh }) {
  const [inCorso, setInCorso] = useState(false);
  const [esito, setEsito] = useState(null);
  const [codice, setCodice] = useState('');
  const [idFiscale, setIdFiscale] = useState('');
  const [guardato, setGuardato] = useState(false);
  const [erroreSalvataggio, setErroreSalvataggio] = useState('');

  const emetti = async () => {
    setInCorso(true);
    setErroreSalvataggio('');
    try {
      const { status, data } = await integrazioniApi.ficEmetti(fattura.id);
      setCodice(data?.codice || '');
      setEsito(esitoEmissione(status, data));
      if (onRefresh) onRefresh();
    } catch (e) {
      // L'esito vive nella RISPOSTA, non nell'eccezione: axios solleva su 4xx/5xx
      // e qui dentro ci sono i tre esiti che non sono «riuscita».
      const status = e.response?.status || 0;
      const data = e.response?.data || {};
      setCodice(data.codice || '');
      setEsito(esitoEmissione(status, data));
      if (onRefresh) onRefresh();
    } finally {
      setInCorso(false);
    }
  };

  // Muro ①: il dato manca e lo chiediamo QUI. Un artigiano in cantiere non sa a
  // memoria una partita IVA, quindi non si chiede nel vocale — si chiede nel
  // momento in cui serve, che è questo.
  const salvaIdFiscaleEriprova = async () => {
    const valore = idFiscale.trim();
    if (!valore) return;
    setInCorso(true);
    setErroreSalvataggio('');
    try {
      // 11 cifre = P.IVA, altrimenti codice fiscale. Non è una validazione
      // fiscale: è instradare il valore nel campo giusto senza chiedere
      // all'artigiano una distinzione che non deve fare lui.
      const campo = /^\d{11}$/.test(valore) ? 'piva' : 'codice_fiscale';
      await clientiApi.update(fattura.cliente, { [campo]: valore });
    } catch (e) {
      setErroreSalvataggio(
        e.response?.data?.detail || 'Non sono riuscito a salvare il dato sul cliente.'
      );
      setInCorso(false);
      return;
    }
    setInCorso(false);
    await emetti();
  };

  // ⛔ Lo stato di emissione si legge DALLA FATTURA, non da questo pannello:
  // il pannello muore con la sessione, la fattura no ([[DBT-122]]).
  const gia = statoEmissioneFIC(fattura);
  const t = esito ? TONI[esito.tipo] || TONI.errore : null;

  return (
    <div style={{ marginBottom: 16 }}>
      {/* ⛔ Su una fattura GIA' emessa il bottone non c'e': ripremerlo e' la
          strada da cui nascono due fatture. Prima del 02/09 tornava a ogni
          ricarica, perche' dipendeva solo dallo stato del pannello. */}
      {mostraBottoneEmetti(esito, gia) && (
        <button
          disabled={inCorso}
          onClick={emetti}
          style={{
            padding: '9px 16px', borderRadius: 'var(--radius-base)', border: 'none',
            background: 'var(--brand)', color: '#fff', fontSize: 13, fontWeight: 500,
            cursor: inCorso ? 'wait' : 'pointer', display: 'inline-flex',
            alignItems: 'center', gap: 6,
          }}
        >
          <Send size={15} strokeWidth={2} />
          {inCorso ? 'Invio in corso…' : 'Emetti su Fatture in Cloud'}
        </button>
      )}

      {/* La riga che RESTA: c'e' oggi, domani e fra sei mesi, e sopravvive a
          una ricarica perche' e' una proprieta' della fattura. */}
      {!esito && gia && (
        <div style={{
          padding: '12px 14px', borderRadius: 'var(--radius-base)',
          border: '1px solid var(--ds-border)', fontSize: 13,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 500 }}>
            <Check size={15} strokeWidth={2} /> {gia.titolo}
          </div>
          <div style={{ marginTop: 6, color: 'var(--ds-muted)' }}>{gia.spiega}</div>
          <div style={{ marginTop: 10 }}>
            <a href={gia.url} target="_blank" rel="noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 14px', borderRadius: 'var(--radius-base)',
                border: '1px solid var(--ds-border)', color: 'var(--ds-text)',
                textDecoration: 'none',
              }}>
              <ExternalLink size={14} strokeWidth={2} /> {gia.azione}
            </a>
          </div>
        </div>
      )}

      {esito && (
        <div style={{
          padding: '12px 14px', borderRadius: 'var(--radius-base)',
          border: `1px solid ${t.bordo}`, fontSize: 13,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: t.testo, fontWeight: 500 }}>
            {esito.tipo === 'riuscita' ? <Check size={15} strokeWidth={2} />
              : <AlertTriangle size={15} strokeWidth={2} />}
            {esito.titolo}
          </div>
          {esito.spiega && (
            <div style={{ marginTop: 6, color: 'var(--ds-muted)' }}>{esito.spiega}</div>
          )}

          {/* Muro ① — il campo che trasforma il muro in un passo */}
          {codice === MURO_DATO_MANCANTE && (
            <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input
                value={idFiscale}
                onChange={(e) => setIdFiscale(e.target.value)}
                placeholder="Partita IVA o codice fiscale"
                style={{
                  flex: '1 1 200px', padding: '8px 10px', fontSize: 13,
                  borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)',
                  background: 'var(--ds-bg)', color: 'var(--ds-text)',
                }}
              />
              <button disabled={inCorso || !idFiscale.trim()} onClick={salvaIdFiscaleEriprova}
                style={{
                  padding: '8px 14px', borderRadius: 'var(--radius-base)', border: 'none',
                  background: 'var(--brand)', color: '#fff', fontSize: 13, fontWeight: 500,
                  cursor: inCorso ? 'wait' : 'pointer',
                }}>
                {esito.azione}
              </button>
            </div>
          )}
          {erroreSalvataggio && (
            <div style={{ marginTop: 8, color: 'var(--ds-danger)' }}>{erroreSalvataggio}</div>
          )}

          {/* Muro ② — la deviazione, con il ritorno garantito */}
          {codice === MURO_FUORI_APP && (
            <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <a href={URL_FIC_CLIENTI} target="_blank" rel="noreferrer"
                style={{
                  padding: '8px 14px', borderRadius: 'var(--radius-base)',
                  border: '1px solid var(--ds-border)', color: 'var(--ds-text)',
                  textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6,
                }}>
                <ExternalLink size={14} strokeWidth={2} /> Apri i clienti su Fatture in Cloud
              </a>
              <button disabled={inCorso} onClick={emetti}
                style={{
                  padding: '8px 14px', borderRadius: 'var(--radius-base)', border: 'none',
                  background: 'var(--brand)', color: '#fff', fontSize: 13, fontWeight: 500,
                  cursor: inCorso ? 'wait' : 'pointer',
                }}>
                {esito.azione}
              </button>
            </div>
          )}

          {/* Muro con un indirizzo e SENZA riprova (oggi: il plafond).
              Il bottone porta dove il problema si risolve — che non e' qui. */}
          {esito.tipo === 'bloccata' && esito.url && !esito.riprova && (
            <div style={{ marginTop: 10 }}>
              <a href={esito.url} target="_blank" rel="noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', borderRadius: 'var(--radius-base)',
                  border: '1px solid var(--ds-border)', color: 'var(--ds-text)',
                  textDecoration: 'none',
                }}>
                <ExternalLink size={14} strokeWidth={2} /> {esito.azione}
              </a>
            </div>
          )}

          {/* ① RIUSCITA — e la porta per finire il lavoro ([[DBT-115]]).
              Il documento nasce «non firmata e non inviata»: allo SdI non ci
              va nessuno finche' FAT-7 non esiste. Senza questo collegamento
              mAIstro dice una cosa vera e l'artigiano ne capisce un'altra.
              ⚠️ PROVVISORIO: quando FAT-7 esistera', questo bottone va TOLTO. */}
          {esito.tipo === 'riuscita' && esito.url && (
            <div style={{ marginTop: 10 }}>
              <a href={esito.url} target="_blank" rel="noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', borderRadius: 'var(--radius-base)',
                  border: '1px solid var(--ds-border)', color: 'var(--ds-text)',
                  textDecoration: 'none',
                }}>
                <ExternalLink size={14} strokeWidth={2} /> {esito.azione}
              </a>
            </div>
          )}

          {/* ④ l'esito incerto: si esce di qui SOLO dichiarando di aver guardato */}
          {esito.tipo === 'incerto' && (
            <div style={{ marginTop: 10 }}>
              <a href={esito.url || urlDocumentoFIC(null)}
                target="_blank" rel="noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 10,
                  padding: '8px 14px', borderRadius: 'var(--radius-base)',
                  border: '1px solid var(--ds-border)', color: 'var(--ds-text)',
                  textDecoration: 'none',
                }}>
                <ExternalLink size={14} strokeWidth={2} /> {esito.azione}
              </a>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--ds-muted)' }}>
                <input type="checkbox" checked={guardato}
                  onChange={(e) => setGuardato(e.target.checked)} />
                Ho controllato su Fatture in Cloud: la fattura non c’è
              </label>
              <button disabled={!guardato || inCorso} onClick={emetti}
                style={{
                  marginTop: 8, padding: '8px 14px', borderRadius: 'var(--radius-base)',
                  border: 'none', background: guardato ? 'var(--brand)' : 'var(--ds-border)',
                  color: '#fff', fontSize: 13, fontWeight: 500,
                  cursor: guardato ? 'pointer' : 'not-allowed',
                }}>
                Riprova
              </button>
            </div>
          )}

          {/* Gli altri esiti ritentabili: un riprova semplice */}
          {mostraBottoneRiprova(esito, codice) && (
            <button disabled={inCorso} onClick={emetti}
              style={{
                marginTop: 10, padding: '8px 14px', borderRadius: 'var(--radius-base)',
                border: 'none', background: 'var(--brand)', color: '#fff',
                fontSize: 13, fontWeight: 500, cursor: inCorso ? 'wait' : 'pointer',
              }}>
              {esito.azione || 'Riprova'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
